/**
 * Root flow: sign in → choose workspace → workspace shell.
 * The shell is keyed by user and workspace so a switch rebuilds all scoped state.
 * After sign-in the account's saved appearance replaces this device's copy.
 */
import { WorkspaceInvite, takeWorkspaceInvite } from './workspace/WorkspaceInvite.tsx';
import { EMPTY_ROUTE, currentRoute, pushRoute, replaceRoute, type Route } from './lib/route.ts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { McpConsent, forgetMcpAuthRequest, takeMcpAuthRequest, type McpAuthRequest } from './auth/McpConsent.tsx';
import { authRejectsSession, classifyFailure, listMyWorkspaces, retryRead, purgeForeignBulkRecords, readMyAccount, recordWorkspaceOpened, saveAppearance, type Account, type FailureKind, type Workspace } from '@miraops/contracts';
import { supabase, transport, type Session } from './lib/session.ts';
import { I18nProvider, createI18n } from './lib/i18n.tsx';
import { appearanceOf, applyPrefs, loadPrefs, savePrefs, type Prefs } from './lib/prefs.ts';
import { SignIn } from './auth/SignIn.tsx';
import { TwoFactorChallenge, TwoFactorRequired, twoFactorNeeded } from './auth/TwoFactor.tsx';
import { WorkspacePicker } from './workspace/WorkspacePicker.tsx';
import { CreateWorkspace } from './workspace/CreateWorkspace.tsx';
import { Shell } from './shell/Shell.tsx';
import { PortalShell } from './portal/PortalShell.tsx';
import { PortalInvite, takePortalToken } from './portal/PortalInvite.tsx';
import {clearForeignBillingDrafts,clearForeignShipmentDrafts} from '@miraops/contracts';
import {Banner,Button,Spinner} from './ui/ui.tsx';
import { UpdateBanner } from './shell/UpdateBanner.tsx';
import { QuietFailureNotice } from './shell/QuietFailureNotice.tsx';
import { quietly } from './lib/quietly.ts';

export interface AccountState { account: Account | null; status: 'loading' | 'ready' | 'failed' }

export function App() {
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [creating, setCreating] = useState(false);
  const [billingCleanupFailed,setBillingCleanupFailed]=useState(false);
  const [notice, setNotice] = useState<string | undefined>();
  const [account, setAccount] = useState<AccountState>({ account: null, status: 'loading' });
  // Whether this session still needs its authenticator code (checked from the session itself, no network).
  const [twoFactor, setTwoFactor] = useState<'checking' | 'none' | 'challenge' | 'enroll'>('checking');

  useEffect(() => { applyPrefs(prefs); savePrefs(prefs); }, [prefs]);

  useEffect(() => {
    void supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) { setSession(null); return; }
      // A stored token can outlive its account. Ask Auth; only a definite rejection (401, 403,
      // 404) signs out. Throttling, server errors and no answer keep the session and drafts.
      const { error } = await supabase.auth.getUser();
      if (error && authRejectsSession(error.status)) {
        await supabase.auth.signOut({ scope: 'local' });
        setSession(null);
      } else {
        setSession(data.session);
      }
    }).catch(error => { quietly(error); setSession(null); }); // Auth reports problems as values; anything thrown shows sign-in, counted
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(prev => {
        // A different user (or sign-out) must never inherit the previous workspace.
        if (prev?.user.id !== next?.user.id) { setWorkspace(null); setAccount({ account: null, status: 'loading' }); }
        return next;
      });
    });
    return () => data.subscription.unsubscribe();
  }, []);

  // Re-checked whenever the session changes (sign-in, code verified, token refresh).
  useEffect(() => {
    if (!session) { setTwoFactor('checking'); return; }
    let live = true;
    void twoFactorNeeded().then(need => { if (live) setTwoFactor(need); }).catch(error => { quietly(error); if (live) setTwoFactor('enroll'); }); // thrown only on a fault: same as an Auth error in twoFactorNeeded
    return () => { live = false; };
  }, [session]);

  const userId = session?.user.id;
  // The database refuses one-step sessions, so account and address loading wait for two-factor.
  const readyUser = twoFactor === 'none' ? userId : undefined;
  // An account answer is used only if the same person is still signed in when it arrives.
  const currentUser = useRef<string | undefined>(undefined);
  currentUser.current = userId;
  const loadAccount = useCallback(async () => {
    const asked = currentUser.current;
    try {
      const next = await readMyAccount(transport);
      if (currentUser.current !== asked) return null;
      setAccount({ account: next, status: 'ready' });
      // Saved appearance wins over this device's copy; intensity and language stay local.
      if (next && next.revision > 0) setPrefs(p => ({ ...p, ...next.appearance }));
      return next;
    } catch {
      if (currentUser.current === asked) setAccount(a => ({ ...a, status: 'failed' }));
      return null;
    }
  }, []);
  // Bulk-send records of anyone else who used this browser are removed at sign-in.
  useEffect(() => { if (userId) { try { purgeForeignBulkRecords(localStorage, userId); } catch { /* storage blocked */ } } }, [userId]);
  // Known account changes clear foreign billing input; transient Auth refresh does not.
  useEffect(()=>{if(userId){try{clearForeignBillingDrafts(localStorage,userId);clearForeignShipmentDrafts(localStorage,userId);setBillingCleanupFailed(false);}catch{setBillingCleanupFailed(true);}}},[userId]);
  useEffect(() => { if (readyUser) void loadAccount(); }, [readyUser, loadAccount]);

  const saveAppearanceFor = useCallback(async (draft: Prefs): Promise<'saved' | 'conflict' | { failed: FailureKind }> => {
    const expected = account.account?.revision ?? 0;
    try {
      const revision = await saveAppearance(transport, expected, appearanceOf(draft));
      setPrefs(draft);
      setAccount(a => a.account ? { ...a, account: { ...a.account, revision, appearance: appearanceOf(draft) } } : a);
      return 'saved';
    } catch (error) {
      const failure = classifyFailure({ error, operation: 'write' });
      if (failure.kind === 'revision_conflict') {
        // Show what is actually saved; the user decides again.
        const fresh = await loadAccount();
        if (fresh) setPrefs(p => ({ ...p, ...fresh.appearance }));
        return 'conflict';
      }
      return { failed: failure.kind };
    }
  }, [account.account?.revision, loadAccount]);

  // Partner portal: an invitation link (?portal_token=…) is reviewed and accepted after
  // sign-in, then the reduced portal opens. A portal participant is never a workspace member.
  const [portal, setPortal] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(() => takePortalToken());
  // A workspace invitation link (?invite=…): reviewed and accepted after sign-in or sign-up.
  const [workspaceInvite, setWorkspaceInvite] = useState<string | null>(() => takeWorkspaceInvite());
  // An AI app signing in through OAuth (?mcp_authorize=1…): consent after sign-in.
  const [mcpAuth, setMcpAuth] = useState<McpAuthRequest | null>(() => takeMcpAuthRequest());
  // The address the app was opened at (a link or a refresh): followed after sign-in. Only IDs.
  const [route, setRoute] = useState<Route>(currentRoute);
  // Opened at a workspace address: a spinner shows until that workspace is checked, instead of the
  // chooser, which would load the workspace list and invitations a second time and flash by.
  const [opening, setOpening] = useState(() => currentRoute().workspaceId !== null);
  const open = useCallback((w: Workspace, at?: Route) => {
    setNotice(undefined);
    setRoute(at ?? { workspaceId: w.id, section: null, record: null });
    setWorkspace(w);
    // "Last opened" is recorded only when someone chooses a workspace, never by following an address
    // or a refresh (opening an address never writes anything).
    if (!at) void recordWorkspaceOpened(transport, w.id).catch(() => { /* ignored: recency is best-effort */ });
  }, []);
  const denied = useCallback(() => {
    setWorkspace(null);
    setNotice(createI18n(prefs.locale).t('state.denied'));
  }, [prefs.locale]);
  // Opens the workspace an address names, if this account can open it; otherwise the chooser with a
  // notice that doesn't say whether the workspace exists.
  const follow = useCallback(async (r: Route) => {
    try {
      if (!r.workspaceId) { setWorkspace(null); return; }
      // On a flaky connection one lost answer shouldn't drop the person back to the chooser.
      const list = await retryRead(() => listMyWorkspaces(transport)).catch(() => null); // ignored: null shows route.checkFailed below
      const w = list?.find(x => x.id === r.workspaceId && x.available);
      if (w) { open(w, r); return; }
      setWorkspace(null);
      // Keep the address when the check failed, so trying the link again (a reload) still works.
      if (list) replaceRoute(EMPTY_ROUTE);
      setNotice(createI18n(prefs.locale).t(list ? 'route.noWorkspace' : 'route.checkFailed'));
    } finally { setOpening(false); }
  }, [open, prefs.locale]);
  const followed = useRef<string | null>(null);
  useEffect(() => {
    if (!readyUser || followed.current === readyUser) return;
    followed.current = readyUser;
    if (route.workspaceId) void follow(route);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyUser]);
  // Back/Forward while the chooser shows.
  useEffect(() => {
    if (workspace) return;
    const onPop = () => { const r = currentRoute(); if (r.workspaceId) void follow(r); };
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, [workspace, follow]);
  const signOut = useCallback(() => {
    dispatchEvent(new Event('miraops:billing-session-ended'));
    dispatchEvent(new Event('miraops:shipment-session-ended'));
    try{clearForeignBillingDrafts(localStorage,null);clearForeignShipmentDrafts(localStorage,null);}catch{setBillingCleanupFailed(true);}
    // Signing out removes this person's bulk-send records from the browser too.
    try { purgeForeignBulkRecords(localStorage, '\u0000'); } catch { /* storage blocked */ }
    void supabase.auth.signOut(); setOpening(false); setWorkspace(null); setPortal(null); replaceRoute(EMPTY_ROUTE); followed.current = null;
  }, []);
  const email = session?.user.email ?? '';

  return (
    <I18nProvider locale={prefs.locale}>
      <UpdateBanner />
      <QuietFailureNotice />
      {billingCleanupFailed?<Banner tone="danger" action={<Button onClick={()=>{try{clearForeignBillingDrafts(localStorage,userId??null);clearForeignShipmentDrafts(localStorage,userId??null);setBillingCleanupFailed(false);}catch{}}}>{createI18n(prefs.locale).t('state.retry')}</Button>}>{createI18n(prefs.locale).t('invoice.profileCleanup')}</Banner>:session === undefined ? null // ignored: the banner stays and can be retried
        : session === null ? <SignIn prefs={prefs} onPrefs={setPrefs} />
        : twoFactor === 'checking' ? null
        : twoFactor === 'challenge' ? <TwoFactorChallenge email={session.user.email ?? ''} onVerified={() => setTwoFactor('none')} onSignOut={signOut} />
        : twoFactor === 'enroll' ? <TwoFactorRequired email={session.user.email ?? ''} onDone={() => setTwoFactor('none')} onSignOut={signOut} />
        : !workspace && creating ? <CreateWorkspace account={account.account} email={email} prefs={prefs} onPrefs={setPrefs} onSignOut={signOut}
            onCancel={() => setCreating(false)}
            onCreated={id => { void retryRead(() => listMyWorkspaces(transport)).then(list => { setCreating(false); const w = list.find(x => x.id === id); if (w) open(w); }, error => { quietly(error); setCreating(false); }); }} />
        : workspaceInvite ? <WorkspaceInvite token={workspaceInvite} email={email} onSignOut={signOut} onDone={() => setWorkspaceInvite(null)}
            onJoined={id => { setWorkspaceInvite(null); void retryRead(() => listMyWorkspaces(transport)).then(list => { const w = list.find(x => x.id === id); if (w) open(w); }, quietly); }} />
        : inviteToken ? <PortalInvite token={inviteToken} email={email} onAccepted={id => { setInviteToken(null); setPortal(id); }}
            onDone={() => setInviteToken(null)} onSignOut={signOut} />
        : mcpAuth ? <McpConsent request={mcpAuth} email={email} onClose={() => { forgetMcpAuthRequest(); setMcpAuth(null); }} />
        : portal ? <PortalShell invitationId={portal} email={email} onExit={() => setPortal(null)} onSignOut={signOut} />
        : !workspace && opening ? <div className="center pad"><Spinner label={createI18n(prefs.locale).t('state.loading')} /></div>
        : !workspace ? <WorkspacePicker account={account.account} email={email} prefs={prefs} onPrefs={setPrefs} onOpen={open} onOpenPortal={setPortal} onCreate={() => setCreating(true)} onSignOut={signOut} notice={notice} />
        : <Shell key={`${session.user.id}:${workspace.id}`} userId={session.user.id} workspace={workspace} email={email} account={account} prefs={prefs} onPrefs={setPrefs}
            onSaveAppearance={saveAppearanceFor} onSwitch={() => { setWorkspace(null); pushRoute(EMPTY_ROUTE); }} onSignOut={signOut} onDenied={denied}
            route={route} onRoute={r => void follow(r)} />}
    </I18nProvider>
  );
}
